import type { NumberFormat } from './db-types';
import { t } from '@lumen/platform';

const CURRENCY: Partial<Record<NumberFormat, string>> = {
  dollar: 'USD',
  euro: 'EUR',
  pound: 'GBP',
  yen: 'JPY',
  rupee: 'INR',
  won: 'KRW',
  yuan: 'CNY',
  franc: 'CHF',
  real: 'BRL',
  krona: 'SEK',
  ruble: 'RUB',
  canadian_dollar: 'CAD',
  australian_dollar: 'AUD',
};

export const NUMBER_FORMATS: { id: NumberFormat; label: string }[] = [
  { id: 'number', label: t('Number') },
  { id: 'number_with_commas', label: t('Number with commas') },
  { id: 'percent', label: t('Percent') },
  { id: 'dollar', label: t('US dollar') },
  { id: 'canadian_dollar', label: t('Canadian dollar') },
  { id: 'australian_dollar', label: t('Australian dollar') },
  { id: 'euro', label: t('Euro') },
  { id: 'pound', label: t('Pound') },
  { id: 'yen', label: t('Yen') },
  { id: 'ruble', label: t('Ruble') },
  { id: 'rupee', label: t('Rupee') },
  { id: 'won', label: t('Won') },
  { id: 'yuan', label: t('Yuan') },
  { id: 'real', label: t('Real') },
  { id: 'franc', label: t('Franc') },
  { id: 'krona', label: t('Krona') },
];

/**
 * Formats a number for display. `decimals` null/undefined means "default" (as many as needed for
 * plain numbers, the currency's usual digits for money). Percent shows the stored ratio × 100.
 */
export function formatNumber(n: number | null | undefined, format: NumberFormat = 'number', decimals?: number | null): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '';
  const fixed = decimals === null || decimals === undefined ? undefined : { minimumFractionDigits: decimals, maximumFractionDigits: decimals };
  if (format === 'number') return fixed ? n.toFixed(decimals!) : String(Math.round(n * 1e10) / 1e10);
  if (format === 'number_with_commas') return new Intl.NumberFormat('en-US', fixed ?? { maximumFractionDigits: 10 }).format(n);
  if (format === 'percent') return new Intl.NumberFormat('en-US', { style: 'percent', ...(fixed ?? { maximumFractionDigits: 8 }) }).format(n);
  const currency = CURRENCY[format];
  if (currency) return new Intl.NumberFormat('en-US', { style: 'currency', currency, ...(fixed ?? {}) }).format(n);
  return String(n);
}

/** Parses user input into a number; accepts commas, currency symbols and a trailing %. Returns null when not a number. */
export function parseNumber(input: string): number | null {
  const s = input.trim();
  if (!s) return null;
  const pct = s.endsWith('%');
  const cleaned = s.replace(/[,\s$€£¥₹₩₽%]|CHF|R\$|kr|CA\$|A\$/g, '');
  if (!/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(cleaned)) return null;
  const n = Number(cleaned);
  return pct ? n / 100 : n;
}

/** Fill ratio for "Show as" bar/ring (0..1), using `divideBy` as the maximum (default 100, or 1 for percent). */
export function progressRatio(n: number | null, format: NumberFormat = 'number', divideBy?: number): number {
  if (n === null || Number.isNaN(n)) return 0;
  const max = divideBy ?? (format === 'percent' ? 1 : 100);
  return Math.max(0, Math.min(1, n / max));
}
